import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Loader2, Award, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv, printReport } from "../reportExport";

const money = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Stat({ label, value }) {
  return <div className="rounded-xl border border-border p-3"><div className="text-[11px] text-muted-foreground">{label}</div><div className="text-lg font-heading font-bold">{value}</div></div>;
}

export default function PartnerScorecard({ shopId, open, onOpenChange }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !shopId) return;
    setLoading(true);
    api.get(`/admin/metrics/scorecard/${shopId}`).then(({ data }) => setD(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [open, shopId]);

  const exportCsv = () => {
    if (!d) return;
    downloadCsv(`scorecard_${d.name}.csv`, [
      ["Partner Scorecard", d.name], ["State", d.state || ""],
      ["National Rank", `${d.national_rank || "—"} / ${d.national_total}`],
      ["State Rank", `${d.state_rank || "—"} / ${d.state_total}`],
      ["Zones Leased", d.zones_leased], ["Advertisers", d.advertisers],
      ["Revenue Generated", d.revenue_generated], ["Revenue Share Earned", d.revenue_share_earned], [],
      ["Zone", "State", "Class", "Advertisers", "Annual Fee"],
      ...d.zone_performance.map((z) => [z.name, z.state, z.classification, z.advertisers, z.annual_fee]),
    ]);
  };
  const exportPdf = () => {
    if (!d) return;
    const row = (c) => `<tr>${c.map((x) => `<td>${x ?? ""}</td>`).join("")}</tr>`;
    printReport(`Scorecard — ${d.name}`, `
      <h1>${d.name} — Partner Scorecard</h1><div class="muted">${d.state || "—"}</div>
      <div class="kpis">
        <div class="kpi"><b>#${d.national_rank || "—"}</b><span>National rank / ${d.national_total}</span></div>
        <div class="kpi"><b>#${d.state_rank || "—"}</b><span>State rank / ${d.state_total}</span></div>
        <div class="kpi"><b>${d.zones_leased}</b><span>Zones leased</span></div>
        <div class="kpi"><b>${d.advertisers}</b><span>Advertisers</span></div>
        <div class="kpi"><b>${money(d.revenue_generated)}</b><span>Revenue generated</span></div>
        <div class="kpi"><b>${money(d.revenue_share_earned)}</b><span>Share earned</span></div>
      </div>
      <h2>Zone performance</h2><table><tr><th>Zone</th><th>State</th><th>Class</th><th>Advertisers</th><th>Annual fee</th></tr>
      ${d.zone_performance.map((z) => row([z.name, z.state, z.classification, z.advertisers, money(z.annual_fee)])).join("")}</table>`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-scorecard">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-primary" />Partner Scorecard</DialogTitle></DialogHeader>
        {loading || !d ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="text-lg font-heading font-bold">{d.name}</div><div className="text-xs text-muted-foreground">{d.state || "—"}</div></div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={exportCsv} data-testid="scorecard-csv"><FileDown className="w-4 h-4 mr-1" />CSV</Button>
                <Button size="sm" variant="outline" onClick={exportPdf} data-testid="scorecard-pdf"><Printer className="w-4 h-4 mr-1" />PDF</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat label="National rank" value={`#${d.national_rank || "—"} / ${d.national_total}`} />
              <Stat label="State rank" value={`#${d.state_rank || "—"} / ${d.state_total}`} />
              <Stat label="Zones leased" value={d.zones_leased} />
              <Stat label="Advertisers" value={d.advertisers} />
              <Stat label="Revenue generated" value={money(d.revenue_generated)} />
              <Stat label="Share earned" value={money(d.revenue_share_earned)} />
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs text-muted-foreground mb-2">Revenue growth (12 months)</div>
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={d.growth_trend} margin={{ top: 4, right: 6, left: -20, bottom: 0 }}>
                  <defs><linearGradient id="sc-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.5} /><stop offset="100%" stopColor="#34d399" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={1} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip contentStyle={{ background: "#0d1420", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="value" stroke="#34d399" strokeWidth={2} fill="url(#sc-g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Zone performance</div>
              {d.zone_performance.length === 0 ? <p className="text-xs text-muted-foreground">No leased zones.</p> :
                <Table><TableHeader><TableRow><TableHead>Zone</TableHead><TableHead>State</TableHead><TableHead>Class</TableHead><TableHead>Advertisers</TableHead><TableHead>Annual fee</TableHead></TableRow></TableHeader>
                  <TableBody>{d.zone_performance.map((z) => <TableRow key={z.zone_id}><TableCell>{z.name}</TableCell><TableCell>{z.state}</TableCell><TableCell>{z.classification}</TableCell><TableCell>{z.advertisers}</TableCell><TableCell>{money(z.annual_fee)}</TableCell></TableRow>)}</TableBody>
                </Table>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
